import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CreditCard, Clock, ArrowRight } from "lucide-react";
import { API, inr, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FIELD = "w-full border border-stone-300 bg-white rounded-lg px-4 py-3 text-sm outline-none focus:border-[#6A1B29] transition-colors";
const STATUS_COLOR = { New: "bg-blue-50 text-blue-700", "Under Review": "bg-amber-50 text-amber-700", Contacted: "bg-purple-50 text-purple-700", "Location Discussion": "bg-indigo-50 text-indigo-700", Approved: "bg-green-50 text-green-700", Onboarding: "bg-teal-50 text-teal-700", Launched: "bg-green-100 text-green-800", Closed: "bg-stone-100 text-stone-600" };

export default function BusinessHub() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("overview");
  const [enquiry, setEnquiry] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState({ threshold: 0, items: [] });
  const [threshold, setThreshold] = useState("");
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [reqForm, setReqForm] = useState({ product_id: "", required_qty: "", reason: "" });
  const [ticket, setTicket] = useState({ subject: "", message: "" });
  const [f, setF] = useState({ name: user?.name || "", outlet_name: user?.outlet_name || "", mobile: user?.mobile || "", whatsapp: user?.whatsapp || "", city: user?.city || "", state: user?.state || "", current_business: user?.current_business || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [activation, setActivation] = useState(null);
  const [pay, setPay] = useState({ amount: 300000, method: "UPI", reference: "" });
  const setPayF = (k) => (e) => setPay({ ...pay, [k]: e.target.value });

  const loadActivation = () => API.get("/outlet/activation").then((r) => setActivation(r.data)).catch(() => setActivation({ activated: true }));

  const submitPayment = async (e) => {
    e.preventDefault();
    try {
      await API.post("/outlet/payment", { amount: Number(pay.amount), method: pay.method, reference: pay.reference });
      toast.success("Payment recorded — pending admin verification");
      loadActivation();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const loadStock = () => API.get("/outlet/stock").then((r) => { setStock(r.data); setThreshold(r.data.threshold ?? ""); }).catch(() => {});
  const loadOrders = () => API.get("/outlet/orders").then((r) => setOrders(r.data)).catch(() => {});
  const loadRequests = () => API.get("/outlet/stock-requests").then((r) => setRequests(r.data)).catch(() => {});

  useEffect(() => {
    API.get("/my/enquiry").then((r) => setEnquiry(r.data?.lead_id ? r.data : null)).catch(() => {});
    API.get("/my/support").then((r) => setTickets(r.data)).catch(() => {});
    API.get("/products?limit=8").then((r) => setProducts(r.data.items)).catch(() => {});
    API.get("/outlet/summary").then((r) => setSummary(r.data)).catch(() => {});
    loadActivation();
    loadStock(); loadOrders(); loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOrderStatus = async (o, status) => {
    try { await API.put(`/outlet/orders/${o.id}/status`, { status }); toast.success(`Marked ${status}`); loadOrders(); API.get("/outlet/summary").then((r) => setSummary(r.data)); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    try {
      await API.post("/outlet/stock-request", { product_id: reqForm.product_id, required_qty: Number(reqForm.required_qty), reason: reqForm.reason });
      setReqForm({ product_id: "", required_qty: "", reason: "" });
      loadRequests();
      toast.success("Stock request submitted");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const saveStock = async (pid, qty) => {
    try {
      const { data } = await API.put(`/outlet/stock/${pid}`, { qty: Number(qty) });
      setStock((s) => ({ ...s, items: s.items.map((x) => (x.id === pid ? { ...x, qty: data.qty, available: data.available, in_stock: data.in_stock, status: data.available === 0 ? "Out of Stock" : (s.threshold > 0 && data.available <= s.threshold ? "Low Stock" : "Available"), low_stock: s.threshold > 0 && 0 < data.available && data.available <= s.threshold } : x)) }));
      toast.success("Stock updated");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const setLocalQty = (pid, qty) => setStock((s) => ({ ...s, items: s.items.map((x) => (x.id === pid ? { ...x, qty: Math.max(0, Number(qty) || 0) } : x)) }));

  const saveThreshold = async () => {
    try {
      const t = Math.max(0, Number(threshold) || 0);
      await API.put("/users/me", { low_stock_threshold: t });
      loadStock();
      toast.success(t > 0 ? `Low-stock alerts on at qty ≤ ${t}` : "Low-stock alerts turned off");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    try { await API.put("/users/me", f); await refresh(); toast.success("Profile updated"); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const raiseTicket = async (e) => {
    e.preventDefault();
    try {
      await API.post("/my/support", ticket);
      setTicket({ subject: "", message: "" });
      const { data } = await API.get("/my/support");
      setTickets(data);
      toast.success("Support request raised");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const TABS = [["overview", "Overview"], ["orders", "Orders"], ["stock", "My Stock"], ["requests", "Stock Requests"], ["enquiry", "My Outlet Enquiry"], ["catalogue", "Product Catalogue"], ["planning", "Outlet Planning"], ["support", "Support"], ["profile", "Profile"]];

  if (activation && !activation.activated) {
    const payment = activation.payment;
    const pending = payment && payment.status === "Pending Verification";
    const rejected = payment && payment.status === "Rejected";
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="business-hub-locked">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Outlet Partner</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 mt-2">Activation Pending</h1>
        <p className="text-stone-500 mt-2">Welcome, {user?.name}. Your Suvikka Outlet Partner dashboard unlocks after your application payment is confirmed by our team.</p>

        <div className="mt-8 space-y-3">
          <div className="bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-3" data-testid="lock-step-application">
            {activation.lead_id ? <CheckCircle2Icon /> : <Clock className="w-5 h-5 text-amber-500" />}
            <div className="flex-1">
              <p className="text-sm font-semibold text-stone-900">1. Outlet Application</p>
              <p className="text-xs text-stone-500">{activation.lead_id ? `Submitted · ${activation.lead_id} · Status: ${activation.lead_status}` : "Not submitted yet"}</p>
            </div>
            {!activation.lead_id && <Link to="/open-outlet" className="text-xs font-semibold text-[#6A1B29] inline-flex items-center gap-1" data-testid="lock-apply-link">Apply now <ArrowRight className="w-3 h-3" /></Link>}
          </div>

          <div className="bg-white border border-stone-200 rounded-xl p-4" data-testid="lock-step-payment">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-[#6A1B29]" />
              <div>
                <p className="text-sm font-semibold text-stone-900">2. Partnership Payment</p>
                <p className="text-xs text-stone-500">Record your payment — our team verifies it and activates your dashboard.</p>
              </div>
            </div>
            {pending && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="lock-payment-pending">
                Payment <strong>{payment.pay_id}</strong> of {inr(payment.amount)} via {payment.method} is <strong>pending verification</strong>. We'll activate your account once confirmed.
              </div>
            )}
            {rejected && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="lock-payment-rejected">
                Payment <strong>{payment.pay_id}</strong> was rejected. Please re-submit with the correct reference or contact support.
              </div>
            )}
            {!pending && activation.lead_id && (
              <form onSubmit={submitPayment} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="lock-payment-form">
                <input required type="number" min="1" placeholder="Amount (₹) *" value={pay.amount} onChange={setPayF("amount")} className={FIELD} data-testid="pay-amount" />
                <select value={pay.method} onChange={setPayF("method")} className={FIELD} data-testid="pay-method">
                  {["UPI", "Bank Transfer", "Cash", "Other"].map((m) => <option key={m}>{m}</option>)}
                </select>
                <input placeholder="Transaction / UPI Reference" value={pay.reference} onChange={setPayF("reference")} className={`${FIELD} sm:col-span-2`} data-testid="pay-reference" />
                <button className="sm:col-span-2 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold py-3 rounded-full text-sm" data-testid="pay-submit">SUBMIT PAYMENT FOR VERIFICATION</button>
              </form>
            )}
          </div>

          <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-stone-400" />
            <div>
              <p className="text-sm font-semibold text-stone-900">3. Admin Confirmation</p>
              <p className="text-xs text-stone-500">Once payment is confirmed, you get full access: Dashboard, Orders, Stock, Inventory, Stock Requests, Reports and Support.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function CheckCircle2Icon() {
    return <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-bold">✓</span>;
  }

  const ORDER_BADGE = { New: "bg-blue-50 text-blue-700", Confirmed: "bg-blue-50 text-blue-700", Preparing: "bg-amber-50 text-amber-700", "Ready for Pickup": "bg-teal-50 text-teal-700", "Rider Assigned": "bg-purple-50 text-purple-700", "Out for Delivery": "bg-indigo-50 text-indigo-700", Delivered: "bg-green-50 text-green-700", "Failed Delivery": "bg-red-50 text-red-600", Cancelled: "bg-stone-100 text-stone-600" };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="business-hub">
      <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Retail Owner Dashboard</p>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 mt-2">SUVIKKA BUSINESS HUB</h1>
      <p className="text-stone-500 mt-1">Welcome, {user?.name}</p>

      <div className="flex gap-2 mt-8 overflow-x-auto pb-1" data-testid="business-hub-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`bh-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${tab === k ? "bg-stone-900 text-white" : "bg-white border border-stone-300 text-stone-700"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "overview" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ["Enquiry Status", enquiry?.status || "No enquiry yet"],
              ["Preferred Location", enquiry?.preferred_location || enquiry?.city || "—"],
              ["Assigned Team Member", enquiry?.assigned_to || "Will be assigned shortly"],
              ["Next Step", enquiry ? (enquiry.status === "New" ? "Our team will contact you" : enquiry.status) : "Submit an outlet enquiry"],
              ["Application Status", enquiry?.status || "Not started"],
              ["Enquiry ID", enquiry?.lead_id || "—"],
            ].map(([l, v], i) => (
              <div key={i} className="bg-white border border-stone-200 rounded-xl p-5" data-testid={`bh-overview-${i}`}>
                <p className="text-xs text-stone-400 uppercase tracking-wider">{l}</p>
                <p className="font-semibold text-stone-900 mt-1.5">{v}</p>
              </div>
            ))}
            {!enquiry && <Link to="/open-outlet" data-testid="bh-start-enquiry" className="sm:col-span-2 lg:col-span-3 inline-flex justify-center bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold py-3.5 rounded-full">SUBMIT OUTLET ENQUIRY</Link>}
          </div>
        )}

        {tab === "enquiry" && (
          <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="bh-enquiry-panel">
            {!enquiry ? <p className="text-stone-500 text-sm">No enquiry found for your account. <Link to="/open-outlet" className="text-[#6A1B29] font-medium">Submit one now</Link>.</p> : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
                {[["Enquiry ID", enquiry.lead_id], ["Location", `${enquiry.preferred_location || ""} ${enquiry.city}, ${enquiry.state}`],
                  ["Date", new Date(enquiry.created_at).toLocaleDateString("en-IN")], ["Assigned Representative", enquiry.assigned_to || "Pending assignment"],
                  ["Follow-up Date", enquiry.follow_up_date || "—"], ["Remarks", enquiry.remarks || "—"]].map(([l, v]) => (
                  <div key={l}><p className="text-xs text-stone-400 uppercase tracking-wider">{l}</p><p className="font-medium text-stone-900 mt-1">{v}</p></div>
                ))}
                <div><p className="text-xs text-stone-400 uppercase tracking-wider">Status</p>
                  <span className={`inline-block mt-1 text-xs font-semibold px-3 py-1.5 rounded-full ${STATUS_COLOR[enquiry.status] || "bg-stone-100"}`} data-testid="bh-enquiry-status">{enquiry.status}</span></div>
              </div>
            )}
          </div>
        )}

        {tab === "catalogue" && (
          <div>
            <p className="text-sm text-stone-500 mb-4">Browse the Suvikka product catalogue. Retail partner pricing appears on product pages where applicable.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {products.map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} className="bg-white border border-stone-200 overflow-hidden group" data-testid={`bh-product-${p.sku}`}>
                  <div className="aspect-square overflow-hidden bg-stone-100"><img src={p.images?.[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-3"><p className="text-xs text-[#6A1B29] font-semibold uppercase tracking-wider">{p.category}</p>
                    <p className="text-sm font-medium mt-0.5 line-clamp-1">{p.title}</p>
                    <p className="text-sm font-semibold mt-1">{inr(p.selling_price)}</p></div>
                </Link>
              ))}
            </div>
            <Link to="/products" className="inline-block mt-5 text-sm font-semibold text-[#6A1B29]" data-testid="bh-view-catalogue">View full catalogue →</Link>
          </div>
        )}

        {tab === "stock" && (
          <div data-testid="bh-stock-panel">
            <p className="text-sm text-stone-500 mb-4">Set the available quantity for each product at your outlet. An item is automatically marked <strong>Out of Stock</strong> when its quantity reaches 0. Pricing is set by Suvikka; you control your own outlet inventory.</p>

            {stock.items.filter((i) => i.low_stock).length > 0 && (
              <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="low-stock-banner">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Low stock alert — {stock.items.filter((i) => i.low_stock).length} item{stock.items.filter((i) => i.low_stock).length > 1 ? "s" : ""} at or below your threshold</p>
                  <p className="text-xs text-amber-700 mt-1">{stock.items.filter((i) => i.low_stock).slice(0, 5).map((i) => i.title).join(", ")}{stock.items.filter((i) => i.low_stock).length > 5 ? ` +${stock.items.filter((i) => i.low_stock).length - 5} more` : ""}</p>
                </div>
              </div>
            )}

            <div className="mb-4 flex flex-wrap items-center gap-3 bg-white border border-stone-200 rounded-xl p-4" data-testid="threshold-row">
              <label className="text-sm font-medium text-stone-700">Low-stock alert threshold</label>
              <input type="number" min="0" value={threshold} onChange={(e) => setThreshold(e.target.value)}
                className="w-24 border border-stone-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#6A1B29]" data-testid="stock-threshold-input" />
              <button onClick={saveThreshold} className="text-xs font-semibold text-white bg-stone-900 hover:bg-stone-700 px-4 py-2 rounded-lg" data-testid="stock-threshold-save">Save threshold</button>
              <span className="text-xs text-stone-400">Warn when available qty is at or below this number. Set 0 to turn alerts off.</span>
            </div>

            <div className="bg-white border border-stone-200 rounded-2xl overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-stone-200">
                  <th className="p-3">Product</th><th className="p-3">Category</th><th className="p-3">Available Qty</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
                <tbody>
                  {stock.items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-stone-500">No products available yet.</td></tr>}
                  {stock.items.map((it) => (
                    <tr key={it.id} className={`border-b border-stone-100 ${it.low_stock ? "bg-amber-50/50" : ""}`} data-testid={`stock-row-${it.sku}`}>
                      <td className="p-3"><div className="flex items-center gap-3"><img src={it.image} alt="" className="w-10 h-10 object-cover rounded" /><span className="font-medium">{it.title}</span></div></td>
                      <td className="p-3 text-stone-600">{it.category}</td>
                      <td className="p-3">
                        <div className="flex items-center border border-stone-300 rounded-full w-fit">
                          <button onClick={() => setLocalQty(it.id, it.qty - 1)} className="px-2.5 py-1.5 text-stone-600" data-testid={`stock-dec-${it.sku}`}>−</button>
                          <input type="number" value={it.qty} onChange={(e) => setLocalQty(it.id, e.target.value)} className="w-14 text-center text-sm outline-none" data-testid={`stock-qty-${it.sku}`} />
                          <button onClick={() => setLocalQty(it.id, it.qty + 1)} className="px-2.5 py-1.5 text-stone-600" data-testid={`stock-inc-${it.sku}`}>+</button>
                        </div>
                      </td>
                      <td className="p-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${it.status === "Out of Stock" ? "bg-red-50 text-red-600" : it.status === "Low Stock" ? "bg-amber-50 text-amber-700" : it.status === "Temporarily Disabled" ? "bg-stone-100 text-stone-500" : "bg-green-50 text-green-700"}`} data-testid={`stock-status-${it.sku}`}>{it.status || (it.qty === 0 ? "Out of Stock" : "Available")}</span>{it.reserved > 0 && <span className="block text-[10px] text-stone-400 mt-0.5">{it.reserved} reserved · {it.available} free</span>}</td>
                      <td className="p-3"><button onClick={() => saveStock(it.id, it.qty)} className="text-xs font-semibold text-white bg-[#6A1B29] hover:bg-[#501325] px-3 py-1.5 rounded-lg" data-testid={`stock-save-${it.sku}`}>Save</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="bh-orders-panel">
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                {[["Today's Orders", summary.today_orders], ["Today's Sales", inr(summary.today_sales)], ["Pending", summary.pending], ["Ready", summary.ready], ["Completed", summary.completed]].map(([l, v]) => (
                  <div key={l} className="bg-white border border-stone-200 rounded-xl p-4" data-testid={`bh-summary-${l.toLowerCase().replace(/[^a-z]/g, "")}`}>
                    <p className="text-[11px] text-stone-400 uppercase tracking-wider">{l}</p>
                    <p className="font-display text-xl font-bold text-stone-900 mt-1">{v}</p>
                  </div>
                ))}
              </div>
            )}
            <p className="text-sm text-stone-500 mb-3">Orders routed to your outlet. Customer personal details are handled by Suvikka — you only prepare the order and mark it ready for pickup.</p>
            <div className="bg-white border border-stone-200 rounded-2xl overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-stone-200">
                  <th className="p-3">Order</th><th className="p-3">Items</th><th className="p-3">Qty</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Action</th></tr></thead>
                <tbody>
                  {orders.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-stone-500">No orders yet.</td></tr>}
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-stone-100" data-testid={`bh-order-${o.order_id}`}>
                      <td className="p-3"><p className="font-medium">{o.order_id}</p><p className="text-xs text-stone-400">{new Date(o.created_at).toLocaleDateString("en-IN")} · {o.payment_method}</p></td>
                      <td className="p-3 text-stone-600 text-xs max-w-[220px]">{o.items.map((i) => `${i.title} ×${i.qty}`).join(", ")}</td>
                      <td className="p-3">{o.item_count}</td>
                      <td className="p-3 font-semibold">{inr(o.amount)}</td>
                      <td className="p-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${ORDER_BADGE[o.status] || "bg-stone-100"}`}>{o.status}</span></td>
                      <td className="p-3">
                        {["New", "Confirmed"].includes(o.status) && <button onClick={() => setOrderStatus(o, "Preparing")} className="text-xs font-semibold text-white bg-amber-600 px-3 py-1.5 rounded-lg" data-testid={`bh-prepare-${o.order_id}`}>Start Preparing</button>}
                        {o.status === "Preparing" && <button onClick={() => setOrderStatus(o, "Ready for Pickup")} className="text-xs font-semibold text-white bg-teal-600 px-3 py-1.5 rounded-lg" data-testid={`bh-ready-${o.order_id}`}>Mark Ready</button>}
                        {!["New", "Confirmed", "Preparing"].includes(o.status) && <span className="text-xs text-stone-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "requests" && (
          <div data-testid="bh-requests-panel" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <form onSubmit={submitRequest} className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3 h-fit" data-testid="bh-request-form">
              <h3 className="font-display text-lg font-semibold">Request Stock</h3>
              <select required value={reqForm.product_id} onChange={(e) => setReqForm({ ...reqForm, product_id: e.target.value })} className={FIELD} data-testid="req-product">
                <option value="">Select product</option>
                {stock.items.map((i) => <option key={i.id} value={i.id}>{i.title} (have {i.qty})</option>)}
              </select>
              <input required type="number" min="1" placeholder="Required quantity" value={reqForm.required_qty} onChange={(e) => setReqForm({ ...reqForm, required_qty: e.target.value })} className={FIELD} data-testid="req-qty" />
              <textarea placeholder="Reason (optional)" rows={2} value={reqForm.reason} onChange={(e) => setReqForm({ ...reqForm, reason: e.target.value })} className={FIELD} data-testid="req-reason" />
              <button className="bg-stone-900 text-white font-semibold px-6 py-2.5 rounded-full text-sm" data-testid="req-submit">SUBMIT REQUEST</button>
            </form>
            <div className="lg:col-span-2 bg-white border border-stone-200 rounded-2xl overflow-x-auto h-fit">
              <table className="w-full text-sm min-w-[520px]">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-stone-200">
                  <th className="p-3">Request</th><th className="p-3">Product</th><th className="p-3">Required</th><th className="p-3">Approved</th><th className="p-3">Status</th></tr></thead>
                <tbody>
                  {requests.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-stone-500">No stock requests yet.</td></tr>}
                  {requests.map((r) => (
                    <tr key={r.id} className="border-b border-stone-100" data-testid={`req-row-${r.req_id}`}>
                      <td className="p-3 font-medium">{r.req_id}</td>
                      <td className="p-3 text-stone-600">{r.product_title}</td>
                      <td className="p-3">{r.required_qty}</td>
                      <td className="p-3">{r.approved_qty ?? "—"}</td>
                      <td className="p-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${r.status === "Approved" ? "bg-green-50 text-green-700" : r.status === "Rejected" ? "bg-red-50 text-red-600" : r.status === "Partially Approved" ? "bg-amber-50 text-amber-700" : "bg-stone-100 text-stone-600"}`}>{r.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "planning" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="bh-planning-panel">
            {[["Store Format", "200–800 sq ft high-street or mall format with Suvikka signage and category bays."],
              ["Category Mix", "Cosmetics, intimates, jewellery, bags, belts, accessories, ladies & girls fashion."],
              ["Launch Support", "Opening stock plan, branding kit, billing guidance and launch marketing."],
              ["Ongoing", "Trend updates, new product drops and retail guidance from the Suvikka team."]].map(([t, d]) => (
              <div key={t} className="bg-white border border-stone-200 rounded-xl p-6">
                <p className="font-display text-lg font-semibold text-stone-900">{t}</p>
                <p className="text-sm text-stone-500 mt-2">{d}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "support" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <form onSubmit={raiseTicket} className="bg-white border border-stone-200 rounded-2xl p-6 space-y-3" data-testid="bh-support-form">
              <h3 className="font-display text-lg font-semibold">Raise a Query</h3>
              <input required placeholder="Subject" value={ticket.subject} onChange={(e) => setTicket({ ...ticket, subject: e.target.value })} className={FIELD} data-testid="support-subject" />
              <textarea required placeholder="Describe your query" rows={4} value={ticket.message} onChange={(e) => setTicket({ ...ticket, message: e.target.value })} className={FIELD} data-testid="support-message" />
              <button className="bg-stone-900 text-white font-semibold px-6 py-3 rounded-full text-sm" data-testid="support-submit">SUBMIT QUERY</button>
            </form>
            <div className="space-y-3">
              {tickets.length === 0 && <p className="text-sm text-stone-500" data-testid="support-empty">No support queries yet.</p>}
              {tickets.map((t) => (
                <div key={t.id} className="bg-white border border-stone-200 rounded-xl p-4" data-testid={`ticket-${t.id.slice(0, 6)}`}>
                  <div className="flex justify-between"><p className="font-medium text-sm">{t.subject}</p>
                    <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-full">{t.status}</span></div>
                  <p className="text-sm text-stone-500 mt-1">{t.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "profile" && (
          <form onSubmit={saveProfile} className="bg-white border border-stone-200 rounded-2xl p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl" data-testid="bh-profile-form">
            <input placeholder="Name" value={f.name} onChange={set("name")} className={FIELD} data-testid="bh-profile-name" />
            <input placeholder="Outlet / Store Name (shown to shoppers)" value={f.outlet_name} onChange={set("outlet_name")} className={FIELD} data-testid="bh-profile-outlet-name" />
            <input placeholder="Mobile" value={f.mobile} onChange={set("mobile")} className={FIELD} data-testid="bh-profile-mobile" />
            <input placeholder="WhatsApp" value={f.whatsapp} onChange={set("whatsapp")} className={FIELD} data-testid="bh-profile-whatsapp" />
            <input placeholder="City" value={f.city} onChange={set("city")} className={FIELD} data-testid="bh-profile-city" />
            <input placeholder="State" value={f.state} onChange={set("state")} className={FIELD} data-testid="bh-profile-state" />
            <input placeholder="Current Business" value={f.current_business} onChange={set("current_business")} className={FIELD} data-testid="bh-profile-business" />
            <button className="sm:col-span-2 bg-stone-900 text-white font-semibold py-3 rounded-full text-sm" data-testid="bh-profile-save">SAVE PROFILE</button>
          </form>
        )}
      </div>
    </div>
  );
}
